import { BigNumber } from '@ethersproject/bignumber';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useMemo } from 'react';

import { useCexAccountBalances, useTickPercent } from 'hooks/cex';
import { bn, multiplyBignumbers } from 'tools';

/**
 * Formula: S-D>2*fee_maker
 * S - 'spread_percentage'.
 * D - 'step_size'.
 * fee_maker - commission maker which is in 'Fees' field in Accounts table (chosenAccountFees.maker_fee).
 */
function isProfitableSpreadBN(
  maker_fee_percent: string,
  spreadPercentagePercent: string,
  stepPercentBN: BigNumber,
) {
  if (!maker_fee_percent || !spreadPercentagePercent) return false;

  const diff = bn(spreadPercentagePercent).sub(stepPercentBN);
  return diff.gt(bn(maker_fee_percent).mul(2));
}

type ProfitableSpreadPayload = {
  accountId: Optional<number>;
  spreadPercentage: string;
  // If true, stepSize is percentage; otherwise stepSize is ticks count
  isStepSizePercentage?: boolean;
  stepSize: string;
};

export function useIsProfitableSpread(payload: ProfitableSpreadPayload[]) {
  const { accountBalances } = useCexAccountBalances({
    account_ids: payload.filter(item => !isNil(item.accountId)).map(item => item.accountId!),
  });
  const { tickPercent } = useTickPercent();

  const profitableSpreadMap = useMemo(() => {
    if (isEmpty(accountBalances)) return {};

    return payload.reduce<Record<string, { isProfitable: boolean; stepPercent?: BigNumber }>>(
      (acc, item) => {
        if (!item.accountId) return acc;
        const balance = accountBalances[item.accountId];
        if (balance) {
          const { spreadPercentage } = item;
          // Determine D in percentage: either direct percentage or ticks * tickPercent
          const isPerc = item.isStepSizePercentage === true;
          const ticks = Number(item.stepSize);
          const stepPercentBN = isPerc
            ? bn(item.stepSize)
            : tickPercent != null
              ? multiplyBignumbers([bn(ticks), 18], [bn(tickPercent), 18])
              : undefined;

          if (stepPercentBN) {
            acc[item.accountId] = {
              isProfitable: isProfitableSpreadBN(
                balance.maker_fee,
                spreadPercentage,
                stepPercentBN,
              ),
              stepPercent: stepPercentBN,
            };
          }
        }
        return acc;
      },
      {},
    );
  }, [accountBalances, payload, tickPercent]);

  return { profitableSpreadMap, chosenAccountFees: accountBalances };
}
